import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * Matches docs/architecture/authentication.md's current role set (`Guest`,
 * `User` — `Admin`/`Moderator` are listed there as future) and its rule
 * that the JWT itself carries only `userId`/`role`/`tokenVersion`, never
 * profile data. This is the profile-ish subset the UI actually needs to
 * render (a name, an avatar) — a decoded/hydrated view of "who's logged
 * in," not the token payload itself.
 */
export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: "guest" | "user";
  avatarUrl: string | null;
}

export type AuthStatus = "guest" | "authenticated";

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
}

const initialState: AuthState = {
  status: "guest",
  user: null,
};

/**
 * Deliberately does NOT hold the access token itself — that stays in
 * `src/lib/token-storage.ts` (a plain localStorage wrapper, read directly
 * by `src/lib/axios.ts`'s request interceptor). Keeping the token out of
 * Redux state means it never appears in a Redux DevTools state snapshot,
 * narrowing its exposure surface — consistent with
 * docs/architecture/security-architecture.md's "secrets must never appear
 * in ... frontend [state you'd inspect]" principle, applied to the one
 * place in this app that actually holds a secret.
 *
 * Reducers here stay pure (no localStorage/side effects) — whatever
 * dispatches `clearCredentials` (the future logout handler) is also
 * responsible for calling `clearAccessToken()` separately.
 */
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: AuthUser }>) => {
      state.status = "authenticated";
      state.user = action.payload.user;
    },
    clearCredentials: (state) => {
      state.status = "guest";
      state.user = null;
    },
  },
});

export const { setCredentials, clearCredentials } = authSlice.actions;
export default authSlice.reducer;
